import { Button } from 'components/Button/Button';
import { FormikTextField } from 'components/FormikTextField/FormikTextField';
import { OverlayModal } from 'components/OverlayModal/OverlayModal';
import { OverlayModalBody } from 'components/OverlayModal/OverlayModalBody';
import { OverlayModalFooter } from 'components/OverlayModal/OverlayModalFooter';
import { OverlayModalHeader } from 'components/OverlayModal/OverlayModalHeader';
import { type FormikErrors, useFormik } from 'formik';
import { type FC, useMemo } from 'react';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';

interface Props {
  mode: 'add' | 'edit';
  onClose: () => void;
  onConfirm: (newTabName: string) => void;
  initialLayoutTitle?: string;
  existingLayoutTitles?: string[];
}

const normalizeLayoutTitle = (title: string) => {
  return title.replace(/\s+/g, '').toLowerCase();
};

export interface DashboardLayoutFormValues {
  layoutTitle: string;
}

export const DashboardLayoutTitleDialog: FC<Props> = (props) => {
  const handleSubmit = (values: DashboardLayoutFormValues) => {
    props.onConfirm(values.layoutTitle);
  };

  const normalizedExistingLayoutTitles = useMemo(() => {
    return (
      props.existingLayoutTitles?.map((title) => normalizeLayoutTitle(title)) ??
      []
    );
  }, [props.existingLayoutTitles]);

  const formik = useFormik({
    initialValues: {
      layoutTitle: props.initialLayoutTitle || '',
    },
    //validateOnChange: true,
    validate: (
      values: DashboardLayoutFormValues,
    ): FormikErrors<DashboardLayoutFormValues> => {
      const errors: FormikErrors<DashboardLayoutFormValues> = {};

      if (values.layoutTitle === '') {
        errors.layoutTitle = "Tab name can't be empty";
      }

      if (
        normalizedExistingLayoutTitles.includes(
          normalizeLayoutTitle(values.layoutTitle),
        )
      ) {
        errors.layoutTitle = 'Tab name already exists';
      }

      return errors;
    },
    onSubmit: makeFormikOnSubmit(handleSubmit),
  });

  return (
    <OverlayModal
      onClose={props.onClose}
      contentClassName='manual-site-separation__overlay-content flex flex-col'
    >
      <form onSubmit={formik.handleSubmit}>
        <OverlayModalHeader>
          <h4>{props.mode === 'add' ? 'New tab' : 'Rename tab'}</h4>
        </OverlayModalHeader>
        <OverlayModalBody>
          <div className='relative'>
            <FormikTextField
              name='layoutTitle'
              formik={formik}
              type='text'
              label='Name'
              autoFocus
            />
          </div>
        </OverlayModalBody>
        <OverlayModalFooter>
          <div className='flex justify-between'>
            <Button
              variant='primary'
              onClick={() => {
                formik.submitForm();
              }}
              type='button'
            >
              {props.mode === 'add' ? 'Create' : 'Save'}
            </Button>
            <Button variant='secondary' type='button' onClick={props.onClose}>
              Cancel
            </Button>
          </div>
        </OverlayModalFooter>
      </form>
    </OverlayModal>
  );
};
